import * as Api from '@ViewModels';
import * as React from 'react';
import { useCreateServiceJobStatusMutation, useServiceJobStatusAutocompleteQuery } from '../../../../../queries';
import { Button } from '../../../../components-v2/Button';
import {
	Combobox,
	ComboboxContent,
	ComboboxEmpty,
	ComboboxInput,
	ComboboxItem,
	ComboboxList,
} from '../../../../components-v2/Combobox';
import { styleSheet } from '../../SearchComponent/styles';

export const ServiceJobStatusSearch = ({
	onSelectStatus,
	canAdd = true,
	onCreateStatus,
	initialSearchFragment,
	onClear,
}: {
	onSelectStatus: (status: Api.IServiceJobStatus) => void;
	canAdd?: boolean;
	onCreateStatus?: (status: Api.IServiceJobStatus) => void;
	initialSearchFragment?: string;
	onClear?: () => void;
}) => {
	const [searchValue, setSearchValue] = React.useState(initialSearchFragment ?? '');
	const [value, setValue] = React.useState<Api.IServiceJobStatus | null>(null);

	const addStatusMutation = useCreateServiceJobStatusMutation({
		onSuccess: (result: Api.IServiceJobStatus) => {
			setValue(result);
			setSearchValue(result.name);
			onCreateStatus?.(result);
		},
	});

	const statusQuery = useServiceJobStatusAutocompleteQuery({
		enabled: !!searchValue,
		fragment: searchValue,
		pageSize: 25,
	});

	const statuses = statusQuery.data?.pages.flatMap(p => p.values) ?? [];

	return (
		<Combobox
			items={statuses}
			value={value}
			onValueChange={(status: unknown) => {
				const s = status as Api.IServiceJobStatus;
				setValue(s);
				onSelectStatus(s);
			}}
			inputValue={searchValue}
			onInputValueChange={newValue => {
				setSearchValue(newValue);
				if (!newValue) {
					setValue(null);
					onClear?.();
				}
			}}
			filter={null}
			itemToStringLabel={s => (s as Api.IServiceJobStatus)?.name ?? ''}
			isItemEqualToValue={(a, b) => (a as Api.IServiceJobStatus)?.id === (b as Api.IServiceJobStatus)?.id}
		>
			<ComboboxInput showTrigger={false} showClear placeholder='Search' />
			<ComboboxContent>
				<ComboboxList>
					{statuses.map(status => (
						<ComboboxItem key={status.id} value={status}>
							{status.name}
						</ComboboxItem>
					))}
					<ComboboxEmpty>No matches found</ComboboxEmpty>
					{canAdd && searchValue ? (
						<Button
							variant='link'
							styles={[styleSheet.addNewButton]}
							onClick={() => addStatusMutation.mutate({ status: { name: searchValue } as Api.IServiceJobStatus })}
						>
							+ Add &quot;{searchValue}&quot;
						</Button>
					) : null}
				</ComboboxList>
			</ComboboxContent>
		</Combobox>
	);
};
